import { useEffect, useRef } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const CAT_LABELS = {
  work: "Work", meeting: "Meeting", local_payment: "Local Payment",
  urgent_call: "Urgent Call", work_done: "Work Done", pending_bill: "Pending Bill",
};
const POLL_MS = 60 * 1000;
const LS_KEY = "reminder_notified_v1";

function readNotified() {
  try { return JSON.parse(localStorage.getItem(LS_KEY)) || {}; } catch { return {}; }
}
function writeNotified(map) {
  // keep only the latest 200 entries to avoid unbounded growth
  const entries = Object.entries(map).slice(-200);
  localStorage.setItem(LS_KEY, JSON.stringify(Object.fromEntries(entries)));
}

export default function ReminderNotifier() {
  const navigate = useNavigate();
  const timerRef = useRef(null);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }

    const check = async () => {
      let list;
      try {
        const r = await api.get("/reminders?status=pending");
        list = r.data;
      } catch { return; }
      const now = new Date();
      const notified = readNotified();
      let changed = false;
      for (const rem of list) {
        if (rem.silenced) continue; // user closed/cleared popups for this reminder
        const due = new Date(rem.due_at);
        if (isNaN(due) || due > now) continue;
        const key = `${rem.id}:${rem.due_at}`; // recurring reminders re-notify on each new due date
        if (notified[key]) continue;
        notified[key] = Date.now();
        changed = true;
        const cat = CAT_LABELS[rem.category] || rem.category;
        const body = `${cat} · due ${due.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`;
        if ("Notification" in window && Notification.permission === "granted") {
          try {
            const n = new Notification(`Reminder: ${rem.title}`, { body, tag: key });
            n.onclick = () => { window.focus(); navigate("/app/reminders"); n.close(); };
          } catch { /* ignore */ }
        }
        toast(`Reminder: ${rem.title}`, {
          description: body,
          duration: 10000,
          action: { label: "View", onClick: () => navigate("/app/reminders") },
          cancel: {
            label: "Silence",
            onClick: () => { api.patch(`/reminders/${rem.id}`, { silenced: true }).catch(() => {}); },
          },
        });
      }
      if (changed) writeNotified(notified);
    };

    check();
    timerRef.current = setInterval(check, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [navigate]);

  return null;
}
